import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
	0% { background-position: -240px 0; }
	100% { background-position: 240px 0; }
`;

export const SkeletonRow = styled.div`
	height: 66px;
	width: 100%;
	border-radius: 12px;
	background: linear-gradient(90deg, #f3f5f8 25%, #e4e8ef 50%, #f3f5f8 75%);
	background-size: 200% 100%;
	animation: ${shimmer} 1.5s infinite linear;
`;

export const ModalPaper = styled.div`
	background-color: #ffffff;
	border: 0;
	border-radius: 18px;
	width: min(540px, calc(100% - 24px));
	height: min(600px, 90vh);
	box-shadow: 0 22px 70px rgba(0, 14, 75, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	outline: none;
	position: relative;
`;

export const ModalTop = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 18px;
	align-items: center;
	padding: 21px 22px;
	border-bottom: 1px solid #edf0f4;
`;

export const ModalTitleWrap = styled.div`
	display: flex;
	align-items: center;
	gap: 13px;
`;

export const ModalIcon = styled.span`
	width: 18px;
	height: 18px;
	display: grid;
	place-items: center;
	color: #00b985;

	svg {
		width: 18px;
		height: 18px;
		display: block;
	}
`;

export const ModalTitle = styled.h2`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 22px;
	font-weight: 700;
	margin: 0;
	color: #3d4259;
`;

export const CloseButton = styled.button`
	border: 0;
	background: transparent;
	width: 32px;
	height: 32px;
	font-size: 24px;
	line-height: 1;
	color: #000e4b;
	display: grid;
	place-items: center;
	border-radius: 50%;
	cursor: pointer;
	transition: background-color 0.2s ease;
`;

export const ModalDescription = styled.div`
	padding: 20px 22px 11px;

	p {
		color: #6b7280;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 12px;
		line-height: 1.5;
		margin: 0;
	}
`;

export const ModalContent = styled.div`
	padding: 0 22px 22px;
	overflow-y: auto;
	flex: 1;
	display: flex;
	flex-direction: column;

	/* Chrome, Safari e Edge */
	&::-webkit-scrollbar {
		width: 6px;
	}
	&::-webkit-scrollbar-thumb {
		background-color: #e4e7ed;
		border-radius: 10px;
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}

	/* Firefox */
	scrollbar-width: thin;
	scrollbar-color: #e4e7ed transparent;
`;

export const PatientSearch = styled.div`
	height: 44px;
	border-bottom: 1px solid #e4e7ed;
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 9px;

	svg {
		width: 18px;
		height: 18px;
		color: #000e4b;
	}

	input {
		border: 0;
		outline: 0;
		width: 100%;
		color: #000e4b;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 13px;
		background: transparent;

		&::placeholder {
			color: #a3abc0;
		}
	}
`;

export const ContactNotice = styled.p`
	margin: 0 2px 16px;
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 10px;
	line-height: 1.45;
	color: #7a8499;
`;

export const PatientList = styled.div`
	display: grid;
	gap: 10px;
`;

export const PatientItem = styled.article`
	border: 1px solid #e2e5eb;
	border-radius: 12px;
	background: #ffffff;
	padding: 11px 12px;
	transition: border-color 0.2s ease;
`;

export const PatientSummary = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: center;
	gap: 12px;
`;

export const ListAvatar = styled.span<{ hasPhoto?: boolean }>`
	width: 42px;
	height: 42px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	font-family: "NotoSans-Bold", sans-serif;
	color: #ffffff;
	font-size: 11px;
	font-weight: 800;
	border: 1px solid #cfd5df;
	background: #000e4b;
	overflow: hidden;
`;

export const ListPhoto = styled.img`
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
`;

export const PatientInfo = styled.div`
	display: flex;
	flex-direction: column;
`;

export const ListName = styled.div`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	color: #111827;
	margin-bottom: 4px;
`;

export const ListMeta = styled.div`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 9px;
	color: #6b7280;
`;

export const ContactStatus = styled.div`
	margin-top: 3px;
	font-family: "NotoSans-Medium", sans-serif;
	font-size: 9px;
	color: #008f68;
	display: flex;
	align-items: center;
	gap: 4px;
`;

export const ScheduleButton = styled.button<{
	stateType?: "cancel" | "opened" | "default";
}>`
	border-radius: 7px;
	padding: 9px 14px;
	font-size: 11px;
	font-weight: 700;
	font-family: "NotoSans-Bold", sans-serif;
	cursor: pointer;
	transition: background-color 0.2s ease, border-color 0.2s ease,
		color 0.2s ease;

	${({ stateType }) => {
		if (stateType === "cancel") {
			return `
				border: 1px solid #ff8e89;
				color: #ff342c;
				background: #ffffff;
				&:hover {
					background: #fff5f5;
				}
			`;
		}
		if (stateType === "opened") {
			return `
				border: 1px solid #00a377;
				color: #00a377;
				background: #ffffff;
				cursor: pointer;
				
				&:hover {
					background: #e6f6f2;
				}
			`;
		}
		// Default / Agendar
		return `
			border: 1px solid #d9dde5;
			background: #ffffff;
			color: #151a2b;
			
		`;
	}}
`;

export const ContactPanel = styled.div`
	margin-top: 12px;
	border: 1px solid #e0e4ea;
	background: #fafafa;
	border-radius: 12px;
	padding: 17px;
`;

export const ContactTitle = styled.h3`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	margin: 0 0 8px;
	color: #000e4b;
`;

export const ContactAge = styled.div`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 10px;
	color: #6b7280;
	margin-bottom: 18px;
`;

export const ContactField = styled.div`
	display: grid;
	gap: 7px;
	margin-top: 14px;

	label {
		font-family: "NotoSans-Bold", sans-serif;
		font-size: 10px;
		font-weight: 700;
		color: #000e4b;
	}

	input,
	textarea {
		width: 100%;
		border: 1px solid #d9dde5;
		border-radius: 7px;
		background: #ffffff;
		color: #3d4259;
		outline: 0;
		font-family: "NotoSans-Regular", sans-serif;
		font-size: 11px;
		transition: border-color 0.2s ease, box-shadow 0.2s ease;

		&:focus {
			border-color: #00a377;
		}
	}

	input {
		height: 38px;
		padding: 0 10px 0 34px;
	}

	textarea {
		height: 91px;
		padding: 11px;
		resize: vertical;
		line-height: 1.5;
	}
`;

export const InputShell = styled.div`
	position: relative;
`;

export const WhatsAppIconWrapper = styled.span`
	position: absolute;
	left: 11px;
	top: 50%;
	transform: translateY(-50%);
	color: #00b985;
	font-size: 16px;
	display: flex;
	align-items: center;
	pointer-events: none;
`;

export const FieldError = styled.span`
	font-family: "NotoSans-Regular", sans-serif;
	color: #d93630;
	font-size: 9px;
	margin-top: 4px;
`;

export const SendButton = styled.button`
	width: 100%;
	height: 38px;
	border: 0;
	border-radius: 7px;
	background: #00d084;
	color: #ffffff;
	font-family: "NotoSans-Bold", sans-serif;
	font-weight: 700;
	font-size: 11px;
	margin-top: 16px;
	cursor: pointer;
	transition: background-color 0.2s ease;

	&:hover {
		background: #00b985;
	}

	&:disabled {
		background: #cfd4da;
		cursor: not-allowed;
	}
`;

export const LoadMoreButton = styled.button`
	display: block;
	margin: 26px auto 4px;
	border: 0;
	border-bottom: 1px solid currentColor;
	background: transparent;
	color: #007c68;
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 11px;
	font-weight: 700;
	padding: 0;
	cursor: pointer;
	transition: color 0.2s ease;

	&:hover {
		color: #005a48;
	}
`;

export const NoResults = styled.div`
	font-family: "NotoSans-Regular", sans-serif;
	text-align: center;
	padding: 28px 10px;
	color: #797d8d;
	font-size: 13px;
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
`;
